import React, {Fragment, useEffect} from 'react';

import {toInteger} from 'lodash';
import every from 'lodash/every';
import {useSelector} from 'react-redux';
import {withRouter} from 'react-router';

import {PendingAcceptanceInfoBar} from 'app/acceptance/components/PendingAcceptanceInfoBar';
import {PageWithRightSidebar} from 'app/components/general/DetailPage/PageWithRightSidebar';
import {
  type GenericDocumentPropsForDetailPage,
  RenderWhenDocumentIsGatheredDetailPage,
} from 'app/components/general/DetailPage/RenderWhenDocumentIsGathered';
import {StickyDiagramForProcesses} from 'app/components/general/DetailPage/RightSidebar/StickyDiagramSidebar';
import {StickyComments} from 'app/components/general/DetailPage/RightSidebar/StickyDocumentCommentsSidebar';
import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import VersionInfoBar from 'app/components/general/VersionInfoBar';
import {VersionDiffer} from 'app/components/VersionDiffer';
import {ViewStepsModeTitle} from 'app/general/detailpage/ViewStepsModeTitle';
import {showNoApprovedVersionsAlertBoxIfNotApproved} from 'app/general/structures/document-alerts/NoApprovedVersionsAlertBox';
import {useLanesForDocument} from 'app/lanes/api';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {toggleProcedureAdvancedMode} from 'app/procedures/actions';
import {CollapsedStepsState} from 'app/procedures/components/CollapsedStepsState';
import {ProcedureStep} from 'app/procedures/components/ProcedureDetailPage/ProcedureStep';
import {ProcedureHeaderOptionsDropdown} from 'app/procedures/components/ProcedureHeaderOptionsDropdown';
import {StyledStepsListDIV} from 'app/procedures/components/structures';
import {ViewableChoices} from 'app/procedures/components/ViewableChoices';
import {
  useProcessByHashid,
  useProcessVersionDetail,
  useProcessVersions,
} from 'app/process/api';
import EmptyProcessList from 'app/process/components/EmptyProcessList';
import {ProcessPageSidebar} from 'app/process/components/ProcessPageSidebar';
import {ViewProcessMetadata} from 'app/process/components/ViewProcessMetadata';
import {processOrVersionToContent} from 'app/process/content-converters';
import {getMemoizedProcessOrphanSteps} from 'app/process/selectors';
import {formatPreviousProcessVersion} from 'app/process/utils';
import {ReviewReminderWarning} from 'app/review/components/ReviewReminderWarning';
import {SignoffForm} from 'app/signoffs/components/SignoffForm';
import {SignoffRequiredInfoBar} from 'app/signoffs/components/SignoffRequiredInfoBar';
import {NextStepLinkItem} from 'app/steps/components/NextStepLinkItem';
import {StepLaneItem} from 'app/steps/components/StepLaneItem';
import {sweetAPI} from 'app/store/sweet-api';
import {useModelChangeSubscription} from 'app/subscriptions';
import {scrollIntoStep} from 'app/utils/browser';
import {getProcessName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {whenVisible} from 'app/utils/page-visibility';
import {useAsyncActions} from 'app/utils/redux';
import {
  ACCEPTANCE_RTK_TAGS,
  COMMENT_TAG,
  LANE_RTK_TAGS,
  PROCESS_RTK_TAGS,
} from 'app/utils/rtk-tags';
import {getComparedVersion} from 'app/utils/showdiff';
import {isLastVersion} from 'app/version/utils';

import type {
  DetailPageBaseProps,
  DetailPageRouterProps,
} from 'app/components/general/DetailPage/types';
import type {
  ProcessHashidSerializedType,
  ProcessVersionSerializedType,
} from 'sweet-types';

const rightSidebarWidgets = [StickyDiagramForProcesses, StickyComments];

export function ProcessPage({
  route,
}: DetailPageBaseProps<ProcessHashidSerializedType>) {
  return (
    <RenderWhenDocumentIsGatheredDetailPage
      route={route}
      useHashidHook={useProcessByHashid}
      useSelectedVersionHook={useProcessVersionDetail}
      useVersionsHook={useProcessVersions}
      DetailPage={ProcessPageWithRouter}
    />
  );
}

export function BaseProcessPage({
  route,
  object: process,
  version,
  versions,
  refetch,
  refetchVersions,
  location,
  params,
}: GenericDocumentPropsForDetailPage<ProcessHashidSerializedType> &
  DetailPageRouterProps) {
  const CurrentLeftSidebar = route.props?.CurrentLeftSidebar;
  const CurrentRightSidebar = route.props?.CurrentRightSidebar;
  const {versionid: versionId} = params;

  const {refetch: refetchLanes} = useLanesForDocument({
    object_ids: process.id,
    content_type: process.content_type,
  });

  const canDisplayVersionInterface = versionId != null && version != null;

  const deletingDocument = useSelector(
    (state) => state.toasts.isDeletingDocument
  );

  const advancedMode = useSelector((state) =>
    versionId == null
      ? state.proceduresPage.get('procedureAdvancedMode')
      : false
  );

  const actions = useAsyncActions({
    toggleProcedureAdvancedMode,
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const loadProcess = whenVisible(() => {
    return Promise.all([refetch(), refetchVersions(), refetchLanes()]);
  });

  useModelChangeSubscription(
    (message, {runWhenAny, modelIdMatch, allRelatedMatch}) => {
      if (deletingDocument) {
        return;
      }

      const expectedRelated = {
        content_type: 'process',
        id: process.id,
      };

      runWhenAny(
        message,
        [
          modelIdMatch('process', process.id),
          allRelatedMatch('comment', expectedRelated),
          allRelatedMatch('processversion', expectedRelated),
          allRelatedMatch('review', expectedRelated),
        ],
        () => {
          actions.invalidateTags([
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
            {type: LANE_RTK_TAGS.TAGS, id: 'list'},
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
            {type: PROCESS_RTK_TAGS.HASHID, id: 'list'},
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
            {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
            {type: ACCEPTANCE_RTK_TAGS.TAGS, id: 'list'},
          ]);
          return loadProcess();
        }
      );
      runWhenAny(message, [allRelatedMatch('comment', expectedRelated)], () =>
        // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
        actions.invalidateTags([{type: COMMENT_TAG, id: 'list'}])
      );

      if (canDisplayVersionInterface) {
        const expectedRelatedVersion = {
          content_type: 'processversion',
          id: version.id,
        };
        runWhenAny(
          message,
          [allRelatedMatch('acceptance', expectedRelatedVersion)],
          () => {
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: ACCEPTANCE_RTK_TAGS.TAGS, id: 'list'},
            ]);
          }
        );
      }
    },
    [deletingDocument, version]
  );

  useEffect(() => {
    if (location.hash) {
      // Chrome doesn't scroll into step work without this 250ms interval
      setTimeout(() => scrollIntoStep(location.hash.replace('#', '')), 250);
    }
  }, []);

  const content = processOrVersionToContent(
    canDisplayVersionInterface ? version : null,
    process
  );
  const previousVersion =
    content != null && versionId != null
      ? formatPreviousProcessVersion(
          getComparedVersion(
            content.url,
            versions,
            location.query['selected-version-group']
          )
        ) || null
      : null;

  const orphans = getMemoizedProcessOrphanSteps(
    content.content,
    process.steps_blocked_at
  );

  const managerCannotApprove = () => {
    // Some managers can edit the process, but not every attached procedure
    // block them from approving.
    return (
      process.can_edit &&
      !every(
        content.content.filter((i) => i.type === 'processprocedure'),
        'can_edit'
      )
    );
  };

  return (
    <CollapsedStepsState stepsIds={content.content.map((s) => s.id)}>
      {({
        collapsedSteps,
        numCollapsedSteps,
        toggleAllStepsCollapse,
        toggleStepCollapse,
      }) => (
        <VersionDiffer
          content={content}
          location={location}
          previousVersion={previousVersion}
          versionAvailable={canDisplayVersionInterface}
        >
          {(contentWithDiff, showingDiff, toggleDiff) => {
            const actionButtonsProps = {
              contentType: process.content_type,
              document: process,
              canChange: process.can_change,
              refreshData: loadProcess,
              disableApproval: managerCannotApprove(),
              additionalContent: () => (
                <ProcedureHeaderOptionsDropdown
                  advancedModeEnabled={advancedMode}
                  toggleAdvancedMode={actions.toggleProcedureAdvancedMode}
                  numCollapsedSteps={numCollapsedSteps}
                  onToggleAllStepsCollapse={toggleAllStepsCollapse}
                  stepsLength={contentWithDiff.content.length}
                />
              ),
            };

            return (
              <PageWithRightSidebar
                context={'view'}
                useHashidHook={useProcessByHashid}
                useSelectedVersionHook={useProcessVersionDetail}
                LeftSidebar={ProcessPageSidebar}
                CurrentLeftSidebar={CurrentLeftSidebar}
                CurrentRightSidebar={CurrentRightSidebar}
                rightSidebarPanels={rightSidebarWidgets}
                breadcrumbProps={{
                  docName: getProcessName(content.name),
                }}
                actionArea={
                  canDisplayVersionInterface ? (
                    <GenericDocumentActionButtons
                      pageContext="view-version"
                      isLastVersion={isLastVersion<ProcessVersionSerializedType>(
                        version,
                        versions
                      )}
                      version={version}
                      {...actionButtonsProps}
                    />
                  ) : (
                    <GenericDocumentActionButtons
                      pageContext="view-published"
                      {...actionButtonsProps}
                    />
                  )
                }
                versionbar={
                  <>
                    {canDisplayVersionInterface && (
                      <VersionInfoBar
                        objectTypeName={gt('process')}
                        version={version}
                        className={'sidebar-visible'}
                        showingDiff={showingDiff}
                        toggleDiff={toggleDiff}
                      />
                    )}
                    {!(CurrentLeftSidebar?.sidebarName === 'showAccept') && (
                      <PendingAcceptanceInfoBar
                        document={process}
                        location={location}
                        openedVersion={version}
                      />
                    )}
                    {versionId == null && (
                      <SignoffRequiredInfoBar
                        contentType="proceduregroup"
                        objectId={process.id}
                        viewChangesURL={process.current_version.html_url}
                      />
                    )}
                  </>
                }
              >
                <DocumentPresenceMembers
                  contentType="proceduregroup"
                  objectId={process.id}
                  pageContext="view"
                  contexts={['view', 'edit', 'task']}
                />

                <ViewProcessMetadata
                  content={contentWithDiff}
                  process={process}
                  versionId={toInteger(versionId ?? process.current_version.id)}
                  policies={process.policies}
                  tags={process.tags}
                  advancedModeEnabled={advancedMode}
                  warningComponent={
                    <Fragment>
                      {showNoApprovedVersionsAlertBoxIfNotApproved(
                        'proceduregroup',
                        process.approved_at
                      )}
                      <ReviewReminderWarning
                        reviewDate={process.next_review_at}
                        contentType={'proceduregroup'}
                        versionId={process.current_version.id}
                        versionContentType={'processversion'}
                        canReview={process.can_edit}
                        onReview={loadProcess}
                      />
                    </Fragment>
                  }
                />

                <StyledStepsListDIV className="js-document-body container pt-0">
                  <div className="row">
                    <div className="col pt-4 col-12">
                      <ViewStepsModeTitle
                        title={gt('Procedures List')}
                        numCollapsedSteps={numCollapsedSteps}
                        toggleAllStepsCollapse={toggleAllStepsCollapse}
                      />
                      {contentWithDiff.content.length ? (
                        <ol className="steps mt-4">
                          {contentWithDiff.content.map((step) => (
                            <li key={step.id} id={step.id}>
                              <ProcedureStep
                                index={step.id}
                                step={step}
                                contentType={'processversion'}
                                orphan={orphans.has(step.id)}
                                showCopyLink={versionId == null}
                                collapsed={collapsedSteps[step.id]}
                                onToggleCollapse={toggleStepCollapse}
                                versionId={toInteger(
                                  versionId ?? process.current_version.id
                                )}
                                objectId={process.id}
                                choicesList={
                                  step.type === 'decision' ? (
                                    <ViewableChoices
                                      choices={step.choices}
                                      steps={content.content}
                                      edit={false}
                                    />
                                  ) : null
                                }
                                previous={
                                  <StepLaneItem
                                    stepLaneId={content.lanes[step.id]}
                                    laneDiff={
                                      contentWithDiff.lanesDiff?.[step.id]
                                    }
                                  />
                                }
                                next={
                                  <NextStepLinkItem
                                    step={step}
                                    steps={content.content}
                                  />
                                }
                              />
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <EmptyProcessList
                          processHTMLURL={process.html_url}
                          canChange={process.can_change}
                        />
                      )}
                      {versionId == null && (
                        <SignoffForm
                          objectId={process.id}
                          contentType="proceduregroup"
                          versionId={process.current_version.id}
                          versionContentType="processversion"
                          objectName={process.name}
                        />
                      )}
                    </div>
                  </div>
                </StyledStepsListDIV>
              </PageWithRightSidebar>
            );
          }}
        </VersionDiffer>
      )}
    </CollapsedStepsState>
  );
}
const ProcessPageWithRouter = withRouter(BaseProcessPage);
